import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { isMoment } from "moment";
import { BehaviorSubject, from, Observable, of, Subject } from "rxjs";
import { concatMap, filter, map, mergeMap, startWith, tap, switchMap, shareReplay } from "rxjs/operators";

@Injectable({
	providedIn: 'root'
})
export class RequestService {

	private sharedRequests: Map<string, Observable<any>> = new Map();

	protected get baseUrl (): string { return '' };
	protected get baseHeaders (): {[name: string]: string | string[]} | Observable<{[name: string]: string | string[]}> { return {} };
	protected get beforeRequest (): boolean | Observable<boolean> { return true; } 

	constructor(protected http: HttpClient) { }

	private request (method: string, url: string, options: any = {}): Observable<any> {

		const beforeRequest = this.beforeRequest instanceof Observable ? this.beforeRequest : of(this.beforeRequest);
		const baseHeaders = this.baseHeaders instanceof Observable ? this.baseHeaders : of(this.baseHeaders);

		return beforeRequest.pipe(
            filter(allowed => allowed === true),
            switchMap(() => baseHeaders),
			switchMap(headers => {

				// Headers options surchage
				if(!options.headers) 
					options.headers = new HttpHeaders();

				options.headers.set('Content-Type', 'application/json');

				if(options.ignoreErrorNotifications) {

					if(!options.params)
						options.params = new HttpParams();

					options.params = options.params.set('ignoreErrorNotifications', true);
				}

				for (const headerName in headers)
					options.headers = options.headers.set(headerName, headers[headerName]);

				const safeUrl = `${this.baseUrl}${url}`.replace(/\/$/, '');
				const request = this.http.request(method, `${safeUrl}`, options);

				if(options.shared)
					return this.sharedRequests.get(`${method}/${safeUrl}`) ??
							this.sharedRequests.set(`${method}/${safeUrl}`, request.pipe(shareReplay(1))).get(`${method}/${safeUrl}`);
				else
					return request;
			})
		);
	}

	get (url: string, options: any = {}): Observable<any> { return this.request('get', url, options)}
	put (url: string, body: any, options: any = {}): Observable<any> { return this.request('put', url, {body, ...options})}
	post (url: string, body: any, options: any = {}): Observable<any> { return this.request('post', url, {body, ...options})}
	patch (url: string, body: any, options: any = {}): Observable<any> { return this.request('patch', url, {body, ...options})}
	delete (url: string, options: any = {}): Observable<any> { return this.request('delete', url, options)}

	/**
	 * Stacked requests
	 * @param requests
	 * @returns
	 */
	requests (requests: {method?: string, url: string, options?: any}[], responseOnly: boolean = true): Observable<{
		index: number,
		length: number,
		isLast: boolean,
		isBeforeRequest: boolean,
		response?: any
	}>{
		return from(requests).pipe(
			concatMap((request, index: number) =>
				this.request(request.method ?? 'get', request.url, request.options).pipe(
					startWith({isBeforeRequest: true}),
					map(response => ({
						isBeforeRequest: false,
						index,
						length: requests.length,
						isLast: index === requests.length - 1,
						...(response.isBeforeRequest ? response : {response})
					})),
					filter(response => (responseOnly ? response.isBeforeRequest === false : true))
				)
			)
		);
	}

	/**
	 * Stacked async requests
	 * @param requests
	 * @returns
	 */
	requestsAsync (requests: {method?: string, url: string, options?: any}[], responseOnly: boolean = true): Observable<{
		index: number,
		length: number,
		isLast: boolean,
		isBeforeRequest: boolean,
		asyncCounter: number,
		asyncBeforeRequestCounter: number,
		response?: any
	}>{
		let requestsCounter = [-1, -1];
		return from(requests).pipe(
			mergeMap((request, index: number) =>
				this.request(request.method ?? 'get', request.url, request.options).pipe(
					startWith({isBeforeRequest: true, asyncBeforeRequestCounter: ++requestsCounter[0]}),
					map(response => ({
						isBeforeRequest: false,
						asyncBeforeRequestCounter: requestsCounter[0],
						index,
						length: requests.length,
						asyncCounter: (response.isBeforeRequest ? 0 : ++requestsCounter[1]),
						isLast: (response.isBeforeRequest ? requestsCounter[0] : requestsCounter[1]) === requests.length - 1,
						...(response.isBeforeRequest ? response : {response})
					})),
					filter(response => (responseOnly ? response.isBeforeRequest === false : true))
				)
			)
		);
	}

	/**
	 * Enable pagination for request
	 * @param endPoint 
	 * @param params 
	 */
	paginate (endPoint: string, params?: any): RequestPaginate<any> {
		return new RequestPaginate(this, endPoint, params);
	}

	clearShared (key?: string) {

		let shared = Array.from(this.sharedRequests.keys());

		if(key)
			shared = shared.filter(requestKey => new RegExp(key).test(requestKey));

		shared.each(requestKey => this.sharedRequests.delete(requestKey));

		return this;
	}
}

export class RequestParams {

	private _params: any;

	constructor (params: any) { this._params = params; }

	get value (): any {
		return this._params;
	}

	get params (): any[] {
		return this.flatten(this._params)
	}

	private flatten (object: any, level: number = 0) {
		let flattenedObject: any, flattened: any = {};

		for (let item in object) {

			if (!object.hasOwnProperty(item)) 
				continue;

			// if(Array.isArray(object[item]))
			// 	flattened[(level > 0 ? `[${item}]` : item)] = object[item].join(',');
			
			if(isMoment(object[item]))
				flattened[(level > 0 ? `[${item}]` : item)] = object[item].toISOString();
			else if(object[item] instanceof File)
				flattened[(level > 0 ? `[${item}]` : item)] = object[item];
			else if(object[item] instanceof Object) {
				flattenedObject = this.flatten(object[item], level + 1);
				
				for (let itemIndex in flattenedObject) {
					if (!flattenedObject.hasOwnProperty(itemIndex))
						continue;

					flattened[(level > 0 ? `[${item}]` : item) + (!!isNaN(itemIndex as any) ? itemIndex : '')] = flattenedObject[itemIndex];
				}
			} else if(object[item] !== null && object[item] !== undefined)
				flattened[(level > 0 ? `[${item}]` : item)] = object[item];
		}
		
		return flattened;
	}
}

export class RequestPaginate<T> {

	protected current: {
		current_page: number,
		last_page?: number,
		per_page?: number,
		total?: number,
		statistics?: any
	} = {
		current_page: 1
	};

	protected params: RequestParams;
	protected endPoint: string;
	protected requestService: RequestService;
	protected onPageSubject: Subject<T> = new Subject();
	protected onNavigationSubject: BehaviorSubject<number> = new BehaviorSubject(this.current.current_page);

	state: {loading: boolean} = {loading: false};

	constructor (requestService: RequestService, endPoint: string, params: any = {}) {
		
		this.endPoint = endPoint;
		this.requestService = requestService;

		this.resetParams(params);

		this.onNavigationSubject
			.pipe(
				tap(page => {
					this.state.loading = true;
					this.setParams({page});
				}),
				switchMap(() => this.request())
			)
			.subscribe({
				next: page => this.onRequestSuccess(page),
				complete: () => this.onRequestComplete()
			});
	}

	get index(): number { return this.current?.current_page - 1; }
	get size(): number { return this.current?.per_page; }
	get total(): number { return this.current?.total; }
	get isFirst(): boolean { return this.current?.current_page <= 1 }
	get isLast(): boolean { return this.current?.current_page === this.current?.last_page }
	get statistics(): any { return this.current?.statistics; }

	protected request (): Observable<any> { return this.requestService.get(this.endPoint, {params: this.params.params}); }
	
	protected onRequestSuccess (page): void { 
		this.current = page;
		this.state.loading = false;
		this.onPageSubject.next(page.data);
	}

	protected onRequestComplete (): void { 
		this.state.loading = false;
	}

	onNavigation (): Observable<number> {
		return this.onNavigationSubject.asObservable()
	}

	onPage (): Observable<T> {
		return this.onPageSubject.asObservable()
	}

    getParams (param: string): any {
        return this.params.value[param] ?? null;
    }

	setParams (params: any): RequestPaginate<T> {
		this.params = new RequestParams(this.params.value.mergeDeep(params));
		return this;
	}

	resetParams (params: any): RequestPaginate<T> {
		this.params = new RequestParams(params);
		return this;
	}

	navigate (pageIndex: number): void {

		if(pageIndex === 1)
			delete this.current.last_page;
		
		this.onNavigationSubject.next(pageIndex);
	}

	first (): void { this.navigate(1); }

	next (): void {
		if(this.current.current_page < this.current.last_page)
			this.onNavigationSubject.next(this.current.current_page + 1);
	}

	refresh () {
		this.onNavigationSubject.next(this.current.current_page);
	}

	unsubscribe (): void {
		this.onNavigationSubject.complete();
		this.onPageSubject.complete();
	}
}